import Prism from 'prismjs';
import splitLines from 'split-lines';

export interface CodeTokenArgs<Line> {
    /** Line being decorated */
    line: Line;
    /** Index of the line */
    lineIndex: number;
    /** Start offset in the overall block text for this line */
    lineStart: number;
    /** End offset in the overall block text for this line */
    lineEnd: number;
    /** Start offset in the overall block text for this token */
    start: number;
    /** End offset in the overall block text for this token */
    end: number;
    /** Decoration classes to apply */
    classes: string;
    /** Raw text concerned by this token */
    raw: string;
}

/**
 * Return the list of tokens to highlight a code (code blocks or code in API methods)
 */
export function tokenizeCode<Line, Token>(
    lines: Line[],
    grammarName: string,
    template: {
        /** Return the text of a specific line, without taking the \n in consideration */
        getLineContent: (line: Line) => string;
        createToken: (args: CodeTokenArgs<Line>) => null | undefined | Token;
    }
): Token[] {
    const result: Token[] = [];

    if (!lines.length) {
        return result;
    }

    // Index all lines
    const lineOffsets = new Map<number, [number, number]>();
    const lineContents: string[] = [];
    let blockText = '';
    lines.forEach((line, index) => {
        const lineContent = template.getLineContent(line);
        const lineContentToAppend = lineContent + (index < lines.length - 1 ? '\n' : '');
        lineContents.push(lineContent);
        lineOffsets.set(index, [blockText.length, blockText.length + lineContentToAppend.length]);

        blockText = blockText + lineContentToAppend;
    });

    const pushDecoration = (args: CodeTokenArgs<Line>) => {
        const decoration = template.createToken(args);
        if (decoration) {
            result.push(decoration);
        }
    };

    /** Return the index of the line matching an offset in the text */
    const lookupLineForOffset = (offset: number): number => {
        let lineIndex = 0;
        for (const [lineStart, lineEnd] of lineOffsets.values()) {
            if (offset === lineStart || (lineStart <= offset && offset < lineEnd)) {
                return lineIndex;
            }
            lineIndex++;
        }

        throw new Error(`Offset ${offset} is out of bounds`);
    };

    // Global cursor to indicate current offset in the block text when calling processToken.
    let cursor = 0;

    const processTokenContent = (content: string, classes: string) => {
        const contentLines = splitLines(content);

        let contentLineOffset = cursor;

        contentLines.forEach((contentLine) => {
            const lineIndex = lookupLineForOffset(contentLineOffset);
            const line = lines[lineIndex];
            const [lineStart, lineEnd] = lineOffsets.get(lineIndex)!;

            pushDecoration({
                line,
                lineIndex,
                lineStart,
                lineEnd,
                start: contentLineOffset,
                end: contentLineOffset + contentLine.length,
                classes,
                raw: blockText.slice(contentLineOffset, contentLineOffset + contentLine.length),
            });

            contentLineOffset += contentLine.length + 1;
        });
    };

    const grammar = Prism.languages[grammarName];

    // Syntax is not yet loaded, we process as plain text
    if (!grammar) {
        processTokenContent(blockText, '');
        return result;
    }

    const tokens = Prism.tokenize(blockText, grammar);

    const processToken = (token: string | Prism.Token, classes = '') => {
        if (typeof token === 'string') {
            processTokenContent(token, classes);
            cursor += token.length;
        } else {
            // Compute the classes for this token
            classes += ` ${token.type}`;
            if (token.alias) {
                if (Array.isArray(token.alias)) {
                    classes += ` ${token.alias.join(' ')}`;
                } else {
                    classes += ` ${token.alias}`;
                }
            }
            classes = classes.trim();

            if (typeof token.content === 'string') {
                processTokenContent(token.content, classes);
                cursor += token.content.length;
            } else {
                // Nested tokens
                if (Array.isArray(token.content)) {
                    token.content.forEach((nested) => processToken(nested, classes));
                } else {
                    processToken(token.content, classes);
                }
            }
        }
    };

    tokens.forEach((token) => processToken(token));

    return result;
}

/**
 * Group the token per lines.
 */
export function groupTokensByLine<Line>(tokens: CodeTokenArgs<Line>[]): CodeTokenArgs<Line>[][] {
    const result: CodeTokenArgs<Line>[][] = [];
    let currentLine: CodeTokenArgs<Line>[] | undefined;
    tokens.forEach((token) => {
        if (!currentLine || token.lineIndex !== currentLine[0].lineIndex) {
            currentLine = [];
            result.push(currentLine);
        }
        currentLine.push(token);
    });
    return result;
}
